import { useState } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { knownDevices } from '../data/networkData';
import NetworkTopology from '../components/NetworkTopology';

// ── Topology Data ──────────────────────────────────────────────

const ZONES = [
  { id: 'internet', label: 'Internet',          x: 30,  y: 80,  w: 160, h: 440, color: '#4a5a70', fill: '#0a0e1a' },
  { id: 'dmz',      label: 'DMZ',               x: 220, y: 80,  w: 280, h: 440, color: '#ff9800', fill: '#1a0f00' },
  { id: 'internal', label: 'Internal Network',  x: 530, y: 80,  w: 460, h: 440, color: '#00bcd4', fill: '#00131a' },
];

const NODES = [
  { id: 'internet',  label: 'Internet',    sub: '',               ip: '',             zone: 'internet', x: 110, y: 270, online: true,  isCloud: true },
  { id: 'fw01',      label: 'FW-EDGE-01',  sub: 'Firewall',       ip: '10.2.0.1',     zone: 'dmz',      x: 360, y: 190, online: true,  isCloud: false },
  { id: 'web01',     label: 'WEB-01',      sub: 'Web Server',     ip: '10.2.0.10',    zone: 'dmz',      x: 290, y: 330, online: true,  isCloud: false },
  { id: 'proxy01',   label: 'PROXY-01',    sub: 'Reverse Proxy',  ip: '10.2.0.11',    zone: 'dmz',      x: 430, y: 330, online: false, isCloud: false },
  { id: 'swcore',    label: 'SW-CORE-01',  sub: 'Core Switch',    ip: '10.1.0.1',     zone: 'internal', x: 660, y: 270, online: true,  isCloud: false },
  { id: 'dc01',      label: 'SRV-DC01',    sub: 'Domain Ctrl',    ip: '10.1.1.10',    zone: 'internal', x: 790, y: 180, online: true,  isCloud: false },
  { id: 'app01',     label: 'SRV-APP01',   sub: 'App Server',     ip: '10.1.1.11',    zone: 'internal', x: 790, y: 310, online: true,  isCloud: false },
  { id: 'ws001',     label: 'WS-001',      sub: 'Workstation',    ip: '10.1.2.100',   zone: 'internal', x: 920, y: 390, online: true,  isCloud: false },
];

const CONNECTIONS = [
  { from: 'internet', to: 'fw01' },
  { from: 'fw01',     to: 'web01' },
  { from: 'fw01',     to: 'proxy01' },
  { from: 'fw01',     to: 'swcore' },
  { from: 'swcore',   to: 'dc01' },
  { from: 'swcore',   to: 'app01' },
  { from: 'swcore',   to: 'ws001' },
];

const nodeMap = Object.fromEntries(NODES.map(n => [n.id, n]));

function NodeBox({ node, onSelectDevice, hovered, onEnter, onLeave, onClick }) {
  if (node.isCloud) {
    return (
      <g>
        <ellipse cx={node.x} cy={node.y - 10} rx={44} ry={30} fill="#1e3a5f" stroke="#4a5a70" strokeWidth={1.5} />
        <ellipse cx={node.x - 20} cy={node.y + 5} rx={30} ry={20} fill="#1e3a5f" stroke="#4a5a70" strokeWidth={1.5} />
        <ellipse cx={node.x + 20} cy={node.y + 5} rx={30} ry={20} fill="#1e3a5f" stroke="#4a5a70" strokeWidth={1.5} />
        <rect x={node.x - 44} y={node.y + 2} width={88} height={16} fill="#1e3a5f" />
        <text x={node.x} y={node.y + 36} textAnchor="middle" fill="#7a8fa6" fontSize={11} fontFamily="monospace">{node.label}</text>
      </g>
    );
  }

  const w = 110; const h = 54;

  return (
    <g>
      <rect x={node.x - w / 2} y={node.y - h / 2} width={w} height={h} rx={4} fill={hovered ? '#0d2137' : '#111827'} stroke={hovered ? '#00bcd4' : '#1e3a5f'} strokeWidth={1.5} />
      <circle cx={node.x - w / 2 + 10} cy={node.y - h / 2 + 10} r={4} fill={node.online ? '#4caf50' : '#f44336'} />
      <text x={node.x} y={node.y - 6} textAnchor="middle" fill={hovered ? '#00bcd4' : '#ffffff'} fontSize={12} fontWeight="700" fontFamily="monospace">{node.label}</text>
      <text x={node.x} y={node.y + 8} textAnchor="middle" fill="#7a8fa6" fontSize={10} fontFamily="sans-serif">{node.sub}</text>
      {node.ip && <text x={node.x} y={node.y + 22} textAnchor="middle" fill="#00bcd4" fontSize={10} fontFamily="monospace">{node.ip}</text>}
      {/* Transparent hit area — pointerEvents="all" required for transparent SVG fills */}
      <rect
        x={node.x - w / 2} y={node.y - h / 2} width={w} height={h} rx={4}
        fill="transparent"
        pointerEvents="all"
        style={{ cursor: node.ip ? 'pointer' : 'default' }}
        onClick={onClick}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      />
    </g>
  );
}

function Arrow({ from, to }) {
  const n1 = nodeMap[from];
  const n2 = nodeMap[to];
  if (!n1 || !n2) return null;
  const dx = n2.x - n1.x;
  const dy = n2.y - n1.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len; const uy = dy / len;
  const x1 = n1.x + ux * 55;
  const y1 = n1.y + uy * 30;
  const x2 = n2.x - ux * 55;
  const y2 = n2.y - uy * 30;

  return (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1e3a5f" strokeWidth={1.5} markerEnd="url(#arrow)" />
  );
}

export default function DmzTopology({ systemId, onSelectDevice, onSelectGroup }) {
  const [lastRefresh] = useState(new Date().toLocaleTimeString());
  const [hoveredNode, setHoveredNode] = useState(null);

  const handleNodeClick = (node) => {
    if (!node.ip) return;
    const entry = knownDevices[node.ip];
    const device = entry
      ? { ...entry, ip: node.ip, ips: [node.ip] }
      : { hostname: node.label, ip: node.ip, ips: [node.ip], os: node.sub, purpose: node.sub, status: node.online ? 'online' : 'offline', type: 'Physical' };
    onSelectDevice(device);
  };

  const zoneCounts = ZONES.map(z => ({ ...z, count: NODES.filter(n => n.zone === z.id).length }));

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>Topology</Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 13 }}>Last refreshed: {lastRefresh}</Typography>
        </Box>

        {/* Zone Stats */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {zoneCounts.map(z => (
            <Paper key={z.id} sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '8px 16px', textAlign: 'center' }}>
              <Typography sx={{ color: z.color, fontWeight: 700, fontSize: 20 }}>{z.count}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>{z.label}</Typography>
            </Paper>
          ))}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, padding: '8px 14px', border: '1px solid #1e3a5f', borderRadius: 2, cursor: 'pointer', color: '#7a8fa6', '&:hover': { color: '#00bcd4', borderColor: '#00bcd4' } }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 13 }}>Refresh</Typography>
          </Box>
        </Box>
      </Box>

      {/* Side-by-side topology panels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

        {/* Left — Network Overview */}
        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 13, padding: '10px 14px', borderBottom: '1px solid #1e3a5f', letterSpacing: 1 }}>
            NETWORK OVERVIEW
          </Typography>
          <div style={{ height: '500px', width: '100%' }}>
            <NetworkTopology onSelectDevice={onSelectDevice} onSelectGroup={onSelectGroup} />
          </div>
        </Paper>

        {/* Right — DMZ Detail */}
        <Paper sx={{ backgroundColor: '#0a0e1a', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
          <Typography sx={{ color: '#ff9800', fontWeight: 700, fontSize: 13, padding: '10px 14px', borderBottom: '1px solid #1e3a5f', letterSpacing: 1 }}>
            DMZ
          </Typography>
          <svg width="100%" viewBox="0 0 1020 540" style={{ display: 'block' }}>
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill="#1e3a5f" />
              </marker>
            </defs>

            {/* Zone backgrounds */}
            {ZONES.map(z => (
              <g key={z.id}>
                <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={8} fill={z.fill} stroke={z.color} strokeWidth={1.5} strokeDasharray="6 3" opacity={0.8} />
                <text x={z.x + z.w / 2} y={z.y + 22} textAnchor="middle" fill={z.color} fontSize={13} fontWeight="700" fontFamily="sans-serif" letterSpacing={2} textTransform="uppercase">
                  {z.label.toUpperCase()}
                </text>
              </g>
            ))}

            {/* Connections */}
            {CONNECTIONS.map((c, i) => <Arrow key={i} from={c.from} to={c.to} />)}

            {/* Nodes */}
            {NODES.map(n => (
              <NodeBox
                key={n.id}
                node={n}
                hovered={hoveredNode === n.id}
                onEnter={() => setHoveredNode(n.id)}
                onLeave={() => setHoveredNode(null)}
                onClick={() => handleNodeClick(n)}
              />
            ))}
          </svg>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 3, padding: '8px 14px', borderTop: '1px solid #1e3a5f' }}>
            {[{ color: '#4caf50', label: 'Online' }, { color: '#f44336', label: 'Offline' }].map(l => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: l.color }} />
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{l.label}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 20, height: 1.5, backgroundColor: '#1e3a5f' }} />
              <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>Connection</Typography>
            </Box>
          </Box>
        </Paper>

      </Box>
    </Box>
  );
}
